import { useState } from 'react';
import {
  TrendingUp,
  DollarSign,
  Target,
  Users,
  Search,
  BarChart3,
  ChevronDown,
  ArrowUpRight,
  CheckCircle2,
} from 'lucide-react';
import { generateWhatSuccessLooksLike, type SuccessMetric } from '@/lib/success-projections';

const ICON_MAP = {
  'trending-up': TrendingUp,
  'dollar-sign': DollarSign,
  'target': Target,
  'users': Users,
  'search': Search,
  'bar-chart': BarChart3,
} as const;

const CONFIDENCE_DOT = {
  high: 'bg-green-500/50',
  medium: 'bg-foreground/25',
  low: 'bg-foreground/15',
} as const;

const CONFIDENCE_LABEL = {
  high: 'Published research',
  medium: 'Industry benchmark',
  low: 'Projected estimate',
} as const;

function getImpactType(impact: string): 'dollar' | 'percentage' | 'roas' | 'positive' | 'neutral' {
  if (impact.startsWith('$')) return 'dollar';
  if (impact.includes('x ROAS') || impact.includes('x vs')) return 'roas';
  if (impact.startsWith('+') || impact.startsWith('-') || /^\d+%/.test(impact)) return 'percentage';
  if (['Already achieving', 'Already tracking', 'Competitive advantage'].includes(impact)) return 'positive';
  return 'neutral';
}

function ImpactBadge({ impact }: { impact: string }) {
  const type = getImpactType(impact);

  if (type === 'positive') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-500/60 whitespace-nowrap shrink-0">
        <CheckCircle2 className="h-3 w-3" />
        {impact}
      </span>
    );
  }

  if (type === 'dollar') {
    return (
      <span className="text-xs font-medium text-green-400 whitespace-nowrap bg-green-500/10 px-2 py-0.5 rounded-full shrink-0">
        {impact}
      </span>
    );
  }

  if (type === 'roas') {
    return (
      <span className="text-xs font-medium text-green-400/80 whitespace-nowrap bg-green-500/8 px-2 py-0.5 rounded-full shrink-0">
        {impact}
      </span>
    );
  }

  if (type === 'percentage') {
    return (
      <span className="text-xs font-medium text-foreground/60 whitespace-nowrap border border-border/80 px-2 py-0.5 rounded-full shrink-0">
        {impact}
      </span>
    );
  }

  return (
    <span className="text-xs text-muted-foreground/60 whitespace-nowrap shrink-0">
      {impact}
    </span>
  );
}

function MetricRow({ metric, isExpanded, onToggle }: {
  metric: SuccessMetric;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const IconComponent = ICON_MAP[metric.icon];

  return (
    <div className="group">
      <button
        onClick={onToggle}
        className="w-full text-left px-6 py-3.5 flex items-start gap-3 hover:bg-secondary/30 transition-colors cursor-pointer"
      >
        {/* Icon */}
        <div className="p-1 rounded-md shrink-0 mt-0.5 bg-green-500/10 text-green-500/70">
          <IconComponent className="h-3.5 w-3.5" />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-foreground">{metric.headline}</p>
            <ImpactBadge impact={metric.impact} />
          </div>

          <div className={`overflow-hidden transition-all duration-200 ${
            isExpanded ? 'max-h-64 opacity-100 mt-1.5' : 'max-h-0 opacity-0'
          }`}>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {metric.projection}
            </p>
            <div className="flex items-center gap-1.5 mt-2">
              <span className={`h-1.5 w-1.5 rounded-full ${CONFIDENCE_DOT[metric.confidence]}`} />
              <span className="text-xs text-muted-foreground/40">
                {CONFIDENCE_LABEL[metric.confidence]}
              </span>
            </div>
          </div>
        </div>

        <ChevronDown className={`h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5 transition-transform duration-200 ${
          isExpanded ? 'rotate-180' : ''
        }`} />
      </button>
    </div>
  );
}

export function WhatSuccessLooksLike({
  segment,
  site,
}: {
  segment: 'overview' | 'vitals' | 'seo' | 'tracking';
  site: any;
}) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(0);

  const projection = generateWhatSuccessLooksLike(segment, site);

  if (!projection.metrics.length) return null;

  return (
    <div className="rounded-xl border border-border/60 overflow-hidden">
      {/* Header — matches WhyItMatters structure, subtle green accent */}
      <div className="bg-secondary/20 px-6 py-5 border-l-2 border-l-green-500/30">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-green-500/10 rounded-lg text-green-500/70 shrink-0 mt-0.5">
            <ArrowUpRight className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {projection.title}
              </span>
            </div>
            <p className="text-base font-medium text-foreground leading-relaxed">
              {projection.summary}
            </p>
          </div>
        </div>
      </div>

      {/* Metric rows */}
      <div className="divide-y divide-border/40">
        {projection.metrics.map((metric, i) => (
          <MetricRow
            key={i}
            metric={metric}
            isExpanded={expandedIdx === i}
            onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="px-6 py-2.5 border-t border-border/40 bg-secondary/10">
        <p className="text-[11px] text-muted-foreground/40 leading-relaxed">
          Rates from published research (Google, Deloitte, Backlinko).{segment !== 'tracking' ? ' Dollar estimates per 1,000 monthly visitors.' : ''}
        </p>
      </div>
    </div>
  );
}
